import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../common/hooks/usePermissions';
import { getList } from '../../common/v4/BackendProvider';
import { toast, ToastContainer } from 'react-toastify';
import ProfileForm from './ProfileForm';
import PasswordForm from './PasswordForm';

const MyProfile = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('profile');
    const { user, isGlobalUser, isClientScoped } = usePermissions();

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const response = await getList('/client/profile');
            setProfile(response.data.data);
        } catch (error) {
            console.error('Error loading profile:', error);
            toast.error('Error loading profile information');
        } finally {
            setLoading(false);
        }
    };

    const handleProfileUpdated = () => {
        loadProfile();
        toast.success('Profile updated successfully');
    };

    const handlePasswordUpdated = () => {
        toast.success('Password updated successfully');
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12">
                        <div className="text-center" style={{ padding: '100px 0' }}>
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12">
                        <div className="text-center" style={{ padding: '100px 0' }}>
                            <p className="text-muted">Unable to load profile information</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-md-12">
                    <div className="page-header">
                        <h1>My Profile</h1>
                        <p className="text-muted">Manage your account information and settings</p>
                    </div>
                </div>
            </div>

            <div className="row">
                {/* Profile Summary Card */}
                <div className="col-md-4">
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Profile Summary</h3>
                        </div>
                        <div className="panel-body">
                            <div className="text-center" style={{ marginBottom: '20px' }}>
                                <div className="avatar" style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '2rem',
                                    fontWeight: 'bold',
                                    margin: '0 auto 15px'
                                }}>
                                    {profile.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                                <h4>{profile.user?.name}</h4>
                                <p className="text-muted">{profile.user?.email}</p>
                            </div>

                            <hr />

                            <div className="profile-info">
                                <div className="row" style={{ marginBottom: '10px' }}>
                                    <div className="col-sm-4">
                                        <strong>Account Type:</strong>
                                    </div>
                                    <div className="col-sm-8">
                                        {isGlobalUser() ? (
                                            <span className="label label-primary">Global User</span>
                                        ) : (
                                            <span className="label label-info">Client User</span>
                                        )}
                                    </div>
                                </div>

                                {isClientScoped() && profile.client && (
                                    <div className="row" style={{ marginBottom: '10px' }}>
                                        <div className="col-sm-4">
                                            <strong>Client:</strong>
                                        </div>
                                        <div className="col-sm-8">
                                            {profile.client.ATRACCTNAME}
                                        </div>
                                    </div>
                                )}

                                <div className="row" style={{ marginBottom: '10px' }}>
                                    <div className="col-sm-4">
                                        <strong>Timezone:</strong>
                                    </div>
                                    <div className="col-sm-8">
                                        {profile.user?.timezone || 'Not set'}
                                    </div>
                                </div>

                                <div className="row" style={{ marginBottom: '10px' }}>
                                    <div className="col-sm-4">
                                        <strong>Phone:</strong>
                                    </div>
                                    <div className="col-sm-8">
                                        {profile.user?.phone || 'Not set'}
                                    </div>
                                </div>

                                <div className="row" style={{ marginBottom: '10px' }}>
                                    <div className="col-sm-4">
                                        <strong>Status:</strong>
                                    </div>
                                    <div className="col-sm-8">
                                        {profile.user?.enabled ? (
                                            <span className="label label-success">Active</span>
                                        ) : (
                                            <span className="label label-danger">Inactive</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Profile Management Tabs */}
                <div className="col-md-8">
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Profile Management</h3>
                        </div>
                        <div className="panel-body">
                            {/* Tab Navigation */}
                            <ul className="nav nav-tabs">
                                <li className={activeTab === 'profile' ? 'active' : ''}>
                                    <a 
                                        href="#profile" 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('profile');
                                        }}
                                    >
                                        Profile Information
                                    </a>
                                </li>
                                <li className={activeTab === 'password' ? 'active' : ''}>
                                    <a 
                                        href="#password" 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('password');
                                        }}
                                    >
                                        Change Password
                                    </a>
                                </li>
                            </ul>

                            {/* Tab Content */}
                            <div className="tab-content" style={{ paddingTop: '20px' }}>
                                {activeTab === 'profile' && (
                                    <div className="tab-pane active">
                                        <ProfileForm 
                                            profile={profile.user} 
                                            onUpdated={handleProfileUpdated}
                                        />
                                    </div>
                                )}

                                {activeTab === 'password' && (
                                    <div className="tab-pane active">
                                        <PasswordForm 
                                            onUpdated={handlePasswordUpdated}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                closeOnClick={true}
                pauseOnHover={true}
                draggable={true}
                progress="undefined"
            />
        </div>
    );
};

export default MyProfile; 